import { useState, type ReactNode } from 'react';
import { Bot, Globe, KeyRound, Plus, Server, ShieldAlert } from 'lucide-react';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '@/api/apiKeys';
import { useWidgets } from '@/api/widgets';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Segmented,
  Skeleton,
  useToast,
} from '@/components/ui';
import { CopyBlock } from '@/components/ui/CopyBlock';
import { env } from '@/lib/env';
import { formatDate, formatRelative } from '@/lib/format';

const API = env.apiUrl;
const MCP_URL = `${API}/api/v1/mcp`;

type Section = 'website' | 'server' | 'mcp';

export function ApiPage() {
  const [section, setSection] = useState<Section>('website');
  const widgets = useWidgets();
  const key = widgets.data?.[0]?.key ?? 'kw_YOUR_WIDGET_ID';

  return (
    <>
      <PageHeader
        title="API & MCP"
        description="Connect your website, backend and AI tools to support — raise queries from your own UI, sync tickets, or plug in an MCP client."
      />

      <ApiKeysCard />

      <div className="mt-8 mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold">Integration guides</h2>
        <Segmented
          label="Guide"
          value={section}
          onChange={setSection}
          options={[
            { value: 'website', label: 'Website (no widget)' },
            { value: 'server', label: 'Backend REST' },
            { value: 'mcp', label: 'MCP server' },
          ]}
        />
      </div>

      {section === 'website' && <WebsiteGuide widgetKey={key} />}
      {section === 'server' && <ServerGuide />}
      {section === 'mcp' && <McpGuide />}
    </>
  );
}

function ApiKeysCard() {
  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
        <span className="bg-ink-950 flex h-10 w-10 items-center justify-center rounded-xl text-white">
          <KeyRound className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">API keys</p>
          <p className="text-xs text-slate-500">
            For server-to-server REST and the MCP server. Keep them on servers only.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> New key
        </Button>
      </div>
      {keys.isPending ? (
        <div className="space-y-2 p-5">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : keys.error ? (
        <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />
      ) : keys.data.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="h-6 w-6" />}
          title="No API keys yet"
          description="Create one for your backend or an MCP client."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {keys.data.map((k) => (
            <li key={k.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                  {k.name}
                  <Badge tone={k.scope === 'write' ? 'amber' : 'blue'}>
                    {k.scope === 'write' ? 'Read & write' : 'Read only'}
                  </Badge>
                  {k.revokedAt && <Badge tone="red">Revoked</Badge>}
                </p>
                <p className="font-mono text-xs text-slate-400">
                  {k.prefix}… · created {formatDate(k.createdAt)} ·{' '}
                  {k.lastUsedAt ? `used ${formatRelative(k.lastUsedAt)}` : 'never used'}
                </p>
              </div>
              {!k.revokedAt && (
                <Button
                  variant={confirm === k.id ? 'danger' : 'ghost'}
                  size="sm"
                  loading={revoke.isPending && confirm === k.id}
                  onClick={() =>
                    confirm === k.id
                      ? revoke.mutate(k.id, {
                          onSuccess: () => (toast.success('Key revoked'), setConfirm(null)),
                          onError: (e) => toast.error(e.message),
                        })
                      : setConfirm(k.id)
                  }
                >
                  {confirm === k.id ? 'Confirm revoke' : 'Revoke'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {creating && <CreateKeyModal onClose={() => setCreating(false)} />}
    </Card>
  );
}

function CreateKeyModal({ onClose }: { onClose: () => void }) {
  const create = useCreateApiKey();
  const toast = useToast();
  const [name, setName] = useState('Main backend');
  const [scope, setScope] = useState<'read' | 'write'>('write');
  const [secret, setSecret] = useState<string | null>(null);

  return (
    <Modal
      open
      onClose={onClose}
      title={secret ? 'Copy your API key' : 'New API key'}
      footer={
        secret ? (
          <Button onClick={onClose}>Done — I’ve stored it</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              onClick={() =>
                create.mutate(
                  { name: name.trim(), scope },
                  { onSuccess: (r) => setSecret(r.key), onError: (e) => toast.error(e.message) },
                )
              }
            >
              Create key
            </Button>
          </>
        )
      }
    >
      {secret ? (
        <div className="space-y-3">
          <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-sm text-amber-800 ring-1 ring-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> This is the only time the full key is shown.
            Store it in your server’s secrets (e.g. <code className="font-mono">KOLLABR_SUPPORT_API_KEY</code>
            ).
          </p>
          <CopyBlock label="API key" icon={<KeyRound className="h-4 w-4" />} value={secret} />
        </div>
      ) : (
        <div className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint="Where it’s used, e.g. “Main backend” or “Claude MCP”."
          />
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-slate-700">Access</span>
            <Segmented
              label="Access"
              value={scope}
              onChange={setScope}
              options={[
                { value: 'read', label: 'Read only' },
                { value: 'write', label: 'Read & write' },
              ]}
            />
            <p className="text-xs text-slate-500">
              Read: look up tickets, customers, articles and KPIs. Write: also create tickets, add
              messages/notes and change status.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Guide({
  icon,
  title,
  intro,
  children,
}: {
  icon: ReactNode;
  title: string;
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-6 p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="from-brand-50 to-accent-50 text-brand-600 ring-brand-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ring-1">
          {icon}
        </span>
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{intro}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

function WebsiteGuide({ widgetKey }: { widgetKey: string }) {
  const identify = `// Signed-in users: identify once (userHash is signed on YOUR backend — see Chat widget → Users).
const API = '${API}/api/v1/public';
const KEY = '${widgetKey}';

const { data: me } = await fetch(\`\${API}/widgets/\${KEY}/identify\`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ userId: user.id, userHash }),
}).then((r) => r.json());
// me.identityToken · me.customer · me.conversations (their tickets, each with a token)`;

  const submit = `// "Report an issue" form on your own page — no chat widget needed.
const res = await fetch(\`\${API}/widgets/\${KEY}/tickets\`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Identity-Token': me.identityToken, // omit for guests and send name + email instead
  },
  body: JSON.stringify({
    subject: 'Payout not received',
    message: 'My Pulse Audio collab was approved 10 days ago…',
    category: 'Payments & payouts', // one of the widget’s categories
    context: { page: location.pathname, campaignId: 'cmp_123' }, // shown to agents
  }),
}).then((r) => r.json());
// → { ticketId, number, token, status }`;

  const follow = `// Show status and replies on your page (poll every few seconds, or on page load)
const { data: thread } = await fetch(\`\${API}/conversations/\${res.data.ticketId}\`, {
  headers: { 'X-Visitor-Token': res.data.token },
}).then((r) => r.json());
// thread.status, thread.messages[] — each { from, kind: 'text'|'ticket'|'article'|'status', meta, body }

// Customer replies from your page:
await fetch(\`\${API}/conversations/\${res.data.ticketId}/messages\`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', 'X-Visitor-Token': res.data.token },
  body: JSON.stringify({ body: 'Here is the screenshot link…' }),
});`;

  return (
    <Guide
      icon={<Globe className="h-5 w-5" />}
      title="Raise queries from kollabr.in without the chat widget"
      intro="Build your own “Contact support” or “Report an issue” screens. The browser calls these public endpoints with your widget id; agents see the query as a card in the inbox."
    >
      <CopyBlock
        label="1 · Identify signed-in users (optional)"
        icon={<Globe className="h-4 w-4" />}
        value={identify}
      />
      <CopyBlock label="2 · Submit a query" icon={<Globe className="h-4 w-4" />} value={submit} />
      <CopyBlock label="3 · Show status & replies" icon={<Globe className="h-4 w-4" />} value={follow} />
      <p className="text-xs text-slate-500">
        These calls are rate-limited per IP and respect the widget’s allowed websites. The full schema is in
        the API docs at{' '}
        <a
          className="text-brand-600 hover:underline"
          href={`${API}/api/docs`}
          target="_blank"
          rel="noreferrer noopener"
        >
          {API}/api/docs
        </a>
        .
      </p>
    </Guide>
  );
}

function ServerGuide() {
  const create = `curl -X POST ${API}/api/v1/integrations/tickets \\
  -H "Authorization: Bearer $KOLLABR_SUPPORT_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "kollabrUserId": "9001",
    "subject": "Payout failed — bank rejected",
    "message": "Auto-raised by the payouts service. UTR: N1234…",
    "category": "Payments & payouts",
    "priority": "high",
    "context": { "payoutId": "po_77", "amount": 12450 }
  }'`;
  const read = `# Everything about a ticket: fields, customer profile, assignee, team, full thread
curl ${API}/api/v1/integrations/tickets/<ticketId> -H "Authorization: Bearer $KOLLABR_SUPPORT_API_KEY"

# A user's tickets (by your platform's user id), and their support profile
curl "${API}/api/v1/integrations/tickets?kollabrUserId=9001&status=open,pending" -H "Authorization: Bearer $KOLLABR_SUPPORT_API_KEY"
curl "${API}/api/v1/integrations/customers/lookup?kollabrUserId=9001" -H "Authorization: Bearer $KOLLABR_SUPPORT_API_KEY"

# Customer replied inside your app → add it to the ticket (or "as": "note" for an internal note)
curl -X POST ${API}/api/v1/integrations/tickets/<ticketId>/messages \\
  -H "Authorization: Bearer $KOLLABR_SUPPORT_API_KEY" -H "Content-Type: application/json" \\
  -d '{ "body": "Uploaded the bank statement", "as": "customer" }'`;
  return (
    <Guide
      icon={<Server className="h-5 w-5" />}
      title="Backend REST API"
      intro="For your own services: open tickets automatically (failed payouts, KYC rejections…), show a user’s tickets in the main app, or forward in-app replies. Authenticate with an API key."
    >
      <CopyBlock
        label="Create a ticket for one of your users"
        icon={<Server className="h-4 w-4" />}
        value={create}
      />
      <CopyBlock label="Read & update" icon={<Server className="h-4 w-4" />} value={read} />
    </Guide>
  );
}

function McpGuide() {
  const claudeCode = `claude mcp add --transport http kollabr-support ${MCP_URL} \\
  --header "Authorization: Bearer ksk_…"`;
  const json = `{
  "mcpServers": {
    "kollabr-support": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer ksk_…" }
    }
  }
}`;
  const sdk = `// In your backend (Node.js) with the official MCP TypeScript SDK
import { Client } from '@modelcontextprotocol/sdk/client/index.js';
import { StreamableHTTPClientTransport } from '@modelcontextprotocol/sdk/client/streamableHttp.js';

const client = new Client({ name: 'kollabr-backend', version: '1.0.0' });
await client.connect(
  new StreamableHTTPClientTransport(new URL('${MCP_URL}'), {
    requestInit: { headers: { Authorization: \`Bearer \${process.env.KOLLABR_SUPPORT_API_KEY}\` } },
  }),
);

const { tools } = await client.listTools();
const ticket = await client.callTool({ name: 'get_ticket', arguments: { number: 1042 } });`;

  const tools: [string, string, 'read' | 'write'][] = [
    ['get_customer', 'Profile, synced platform fields and recent tickets', 'read'],
    ['search_tickets', 'By text/#number, status, priority, channel or customer', 'read'],
    ['get_ticket', 'Full ticket with customer, assignee, team and every message', 'read'],
    ['search_help_articles', 'Published knowledge-base articles', 'read'],
    ['support_overview', 'KPIs: volume, response times, CSAT, SLA, backlog', 'read'],
    ['create_ticket', 'Raise a ticket for a platform user or email', 'write'],
    ['add_ticket_message', 'Customer follow-up or internal note', 'write'],
    ['set_ticket_status', 'Open, pending, on hold, solved, closed', 'write'],
  ];

  return (
    <Guide
      icon={<Bot className="h-5 w-5" />}
      title="MCP server"
      intro="A Model Context Protocol server over Streamable HTTP. Point Claude, your own agents or your backend at it to read everything about a query — and, with a write key, act on it."
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {tools.map(([name, text, scope]) => (
          <div key={name} className="rounded-xl border border-slate-200/80 p-3">
            <p className="flex items-center justify-between gap-2">
              <code className="text-brand-700 font-mono text-[13px] font-semibold">{name}</code>
              <Badge tone={scope === 'write' ? 'amber' : 'blue'}>{scope}</Badge>
            </p>
            <p className="mt-1 text-xs text-slate-500">{text}</p>
          </div>
        ))}
      </div>
      <CopyBlock label="Server URL" icon={<Bot className="h-4 w-4" />} value={MCP_URL} />
      <CopyBlock label="Claude Code" icon={<Bot className="h-4 w-4" />} value={claudeCode} />
      <CopyBlock label="Any MCP client (JSON config)" icon={<Bot className="h-4 w-4" />} value={json} />
      <CopyBlock label="From your backend" icon={<Bot className="h-4 w-4" />} value={sdk} />
      <p className="text-xs text-slate-500">
        Read-only keys only see read tools. Internal notes are included in results, so treat MCP output as
        agent-only.
      </p>
    </Guide>
  );
}
